import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { pLogger } from '@/utils/persistentLogger';

// Extend axios request config to include _retry flag
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

// Helper function to clear localStorage and log to debug_log
function clearAuthAndLog(reason: string, url?: string) {
  console.error(`🚨🚨🚨 [API.TS] CLEARING AUTH - ${reason}`);
  if (url) console.error('🚨 URL:', url);
  console.error('🚨 Stack trace:', new Error().stack);

  // Write to debug_log BEFORE clearing
  try {
    const debugLog = JSON.parse(localStorage.getItem('debug_log') || '[]');
    debugLog.push({
      timestamp: new Date().toISOString(),
      action: `API_CLEAR_${reason.replace(/\s+/g, '_').toUpperCase()}`,
      url: url || 'N/A',
      stack: new Error().stack?.split('\n').slice(0, 5).join('\n')
    });
    localStorage.setItem('debug_log', JSON.stringify(debugLog.slice(-20)));
  } catch {
    // Ignore debug log write failures.
  }

  // Clear auth data
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('expires_in');
  localStorage.removeItem('auth-storage');
  localStorage.removeItem('user_info');
}

// API基础URL配置
// Use relative paths in dev mode for Vite proxy to work
const API_BASE_URL = import.meta.env.VITE_API_URL || '/admin-api';
// 统一使用 /admin-api 以通过 Vite 代理（公开API如登录也在admin-api下）
const PUBLIC_API_BASE_URL = import.meta.env.VITE_PUBLIC_API_URL || '/admin-api';

// 创建axios实例 - 用于受保护的API（需要认证）
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 创建公开axios实例 - 用于公开API（不需要认证，如登录、注册）
const publicApiClient: AxiosInstance = axios.create({
  baseURL: PUBLIC_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 公开API请求拦截器 - 打印请求信息；FormData 时去掉 Content-Type 让浏览器带 boundary
publicApiClient.interceptors.request.use(
  (config) => {
    if (config.data instanceof FormData && config.headers) {
      delete (config.headers as Record<string, unknown>)['Content-Type'];
    }
    console.log('📤📤📤 [publicApiClient] Sending request:', {
      url: config.url,
      method: config.method,
      baseURL: config.baseURL,
      data: config.data,
      headers: config.headers,
      fullURL: `${config.baseURL}${config.url}`
    });
    return config;
  },
  (error) => {
    console.error('🚨 [publicApiClient] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// 请求拦截器 - 添加Token和语言偏好
apiClient.interceptors.request.use(
  (config) => {
    // 上传文件时使用 FormData，必须去掉 Content-Type 让 axios 自动设置 multipart/form-data; boundary=...
    if (config.data instanceof FormData && config.headers) {
      delete (config.headers as Record<string, unknown>)['Content-Type'];
    }

    // 从localStorage获取token
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('access_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      } else if (!token) {
        pLogger.warn('API-REQ', `Request ${config.url} has no token`);
      }

      // 添加语言偏好（与前端默认一致：未切换过则英文）
      const lang = localStorage.getItem('language') || 'en';
      const language = lang === 'zh' ? 'zh-CN' : lang === 'de' ? 'de' : 'en';
      if (config.headers) {
        config.headers['Accept-Language'] = language;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 公开API响应拦截器 - 提取数据
publicApiClient.interceptors.response.use(
  async (response: AxiosResponse) => {
    const responseData = response.data;

    console.log('🔵🔵🔵 [publicApiClient] Received response:', {
      url: response.config.url,
      status: response.status,
      responseData: responseData,
      fullResponse: response
    });

    // 检查业务逻辑是否成功
    if (responseData.success === false || (responseData.code !== undefined && responseData.code !== 0 && responseData.code !== 200)) {
      console.error('❌ [publicApiClient] Business response failed:', {
        code: responseData.code,
        message: responseData.message,
        success: responseData.success
      });
      const error: any = new Error(responseData.message || 'Request failed');
      error.response = response;
      error.code = responseData.code;
      return Promise.reject(error);
    }

    console.log('✅ [publicApiClient] Returning data field:', responseData.data);
    // 直接返回 data 字段，而不是整个 responseData
    return responseData.data;
  },
  (error) => {
    console.error('🚨 [publicApiClient] Request error:', {
      message: error.message,
      response: error.response,
      config: error.config
    });
    return Promise.reject(error);
  }
);

// 响应拦截器 - 统一处理错误
apiClient.interceptors.response.use(
  async (response: AxiosResponse) => {
    const responseData = response.data;

    // Check for token expiration (business code 200204)
    if (responseData.code === 200204) {
      pLogger.error('API-200204', 'Received business code 200204 (token expired)', {
        url: response.config.url,
        message: responseData.message,
      });

      const originalRequest = response.config;

      // Handle token expiration
      if (!originalRequest._retry && typeof window !== 'undefined') {
        const refreshToken = localStorage.getItem('refresh_token');
        const accessToken = localStorage.getItem('access_token');

        pLogger.log('API-200204', 'Checking localStorage state', {
          hasRefreshToken: !!refreshToken,
          refreshTokenLength: refreshToken?.length,
          hasAccessToken: !!accessToken,
          accessTokenLength: accessToken?.length,
        });

        if (!refreshToken) {
          pLogger.error('API-200204', '🚨🚨🚨 No refresh_token; clearing auth and redirecting', {
            url: originalRequest.url,
            message: responseData.message,
          });

          clearAuthAndLog('200204 NO REFRESH TOKEN', originalRequest.url);

          pLogger.error('API-200204', '⚠️⚠️⚠️ About to run: window.location.href = "/login"');
          window.location.href = '/login';
          return Promise.reject(new Error(responseData.message || 'Token expired'));
        }

        if (isRefreshing) {
          pLogger.log('API-200204', 'Refresh already in progress; queueing request');
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient.request(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // 从localStorage获取用户类型以确定正确的refresh endpoint
          const userInfoStr = localStorage.getItem('user_info');
          let userType = 'admin';  // 默认admin
          let refreshEndpoint = '/passport/refresh-token';  // admin endpoint
          let refreshBaseURL = API_BASE_URL;

          if (userInfoStr) {
            try {
              const userInfo = JSON.parse(userInfoStr);
              userType = userInfo.user_type || 'admin';

              // 根据用户类型选择正确的endpoint
              if (userType === 'supplier') {
                refreshEndpoint = '/api/supplier/refresh-token';
                refreshBaseURL = PUBLIC_API_BASE_URL;
              } else if (userType === 'customer') {
                refreshEndpoint = '/api/customer/refresh-token';
                refreshBaseURL = PUBLIC_API_BASE_URL;
              } else {
                refreshEndpoint = '/passport/refresh-token';
                refreshBaseURL = API_BASE_URL;
              }
            } catch (e) {
              pLogger.warn('API-200204', 'Failed to parse user_info, using admin endpoint');
            }
          }

          pLogger.log('API-200204', 'Trying to refresh access_token with refresh_token...', {
            refreshTokenLength: refreshToken.length,
            userType,
            refreshEndpoint,
            refreshBaseURL,
          });

          const refreshInstance = axios.create({
            baseURL: refreshBaseURL,
            timeout: 30000,
          });

          const refreshResponse = await refreshInstance.post(refreshEndpoint, {
            refresh_token: refreshToken,
          });

          pLogger.log('API-200204', `Token refresh response: code=${refreshResponse.data.code}`, {
            code: refreshResponse.data.code,
            success: refreshResponse.data.success,
            message: refreshResponse.data.message,
          });

          if (refreshResponse.data.code === 0 || refreshResponse.data.code === 200 || refreshResponse.data.success) {
            const newAccessToken = refreshResponse.data.data.access_token;
            const newRefreshToken = refreshResponse.data.data.refresh_token;
            const expiresIn = refreshResponse.data.data.expires_in;

            pLogger.log('API-200204', `✅ Token refresh succeeded! New token length: ${newAccessToken?.length}, expires_in: ${expiresIn}s`);

            localStorage.setItem('access_token', newAccessToken);
            localStorage.setItem('refresh_token', newRefreshToken);
            localStorage.setItem('expires_in', expiresIn.toString());

            const expires = new Date();
            expires.setSeconds(expires.getSeconds() + expiresIn);
            document.cookie = `access_token=${newAccessToken}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
            document.cookie = `refresh_token=${newRefreshToken}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;

            processQueue(null, newAccessToken);
            isRefreshing = false;

            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiClient.request(originalRequest);
          } else {
            pLogger.error('API-200204', '❌ Token refresh failed (bad response code)', {
              code: refreshResponse.data.code,
              message: refreshResponse.data.message,
            });

            clearAuthAndLog('200204 REFRESH RESPONSE FAILED', originalRequest.url);
            processQueue(new Error('Token refresh failed'), null);
            isRefreshing = false;

            pLogger.error('API-200204', '⚠️⚠️⚠️ Redirecting to /login (refresh failed)');
            window.location.href = '/login';
            return Promise.reject(new Error(responseData.message || 'Token expired'));
          }
        } catch (refreshError: any) {
          pLogger.error('API-200204', '❌ Token refresh exception (request failed)', {
            message: refreshError?.message,
            status: refreshError?.response?.status,
            data: refreshError?.response?.data,
          });

          clearAuthAndLog('200204 REFRESH EXCEPTION', originalRequest.url);
          processQueue(refreshError, null);
          isRefreshing = false;

          pLogger.error('API-200204', '⚠️⚠️⚠️ Redirecting to /login (refresh exception)');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(new Error(responseData.message || 'Token expired'));
    }

    // 检查业务逻辑是否成功
    // 如果success为false或code不是0/200，则拒绝Promise
    if (responseData.success === false || (responseData.code !== undefined && responseData.code !== 0 && responseData.code !== 200)) {
      const error: any = new Error(responseData.message || 'Request failed');
      error.response = response;
      error.code = responseData.code;
      return Promise.reject(error);
    }

    // 直接返回 data 字段，而不是整个 responseData
    return responseData.data;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      // Token过期，尝试刷新
      if (error.response.status === 401 && !originalRequest._retry) {
        pLogger.error('API-401', 'Received 401 error', {
          url: originalRequest.url,
          method: originalRequest.method,
          response: error.response?.data,
        });

        if (typeof window !== 'undefined') {
          const refreshToken = localStorage.getItem('refresh_token');

          if (!refreshToken) {
            pLogger.error('API-401', '🚨🚨🚨 No refresh_token; clearing auth and redirecting to /login', {
              failedUrl: originalRequest.url,
            });

            clearAuthAndLog('401 NO REFRESH TOKEN', originalRequest.url);

            pLogger.error('API-401', '⚠️⚠️⚠️ About to run: window.location.href = "/login"');
            window.location.href = '/login';
            return Promise.reject(error);
          }

          if (isRefreshing) {
            // 如果正在刷新，将请求加入队列
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return apiClient.request(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            // 从localStorage获取用户类型以确定正确的refresh endpoint
            const userInfoStr = localStorage.getItem('user_info');
            let userType = 'admin';  // 默认admin
            let refreshEndpoint = '/passport/refresh-token';  // admin endpoint
            let refreshBaseURL = API_BASE_URL;

            if (userInfoStr) {
              try {
                const userInfo = JSON.parse(userInfoStr);
                userType = userInfo.user_type || 'admin';

                // 根据用户类型选择正确的endpoint
                if (userType === 'supplier') {
                  refreshEndpoint = '/api/supplier/refresh-token';
                  refreshBaseURL = PUBLIC_API_BASE_URL;
                } else if (userType === 'customer') {
                  refreshEndpoint = '/api/customer/refresh-token';
                  refreshBaseURL = PUBLIC_API_BASE_URL;
                } else {
                  refreshEndpoint = '/passport/refresh-token';
                  refreshBaseURL = API_BASE_URL;
                }
              } catch (e) {
                pLogger.warn('API-REFRESH', 'Failed to parse user_info, using admin endpoint');
              }
            }

            pLogger.log('API-REFRESH', 'Trying to refresh token...', {
              userType,
              refreshEndpoint,
              refreshBaseURL,
            });

            // 创建独立的axios实例用于刷新token，避免拦截器循环
            const refreshInstance = axios.create({
              baseURL: refreshBaseURL,
              timeout: 30000,
            });

            const response = await refreshInstance.post(refreshEndpoint, {
              refresh_token: refreshToken,
            });

            pLogger.log('API-REFRESH', `Token refresh response: code=${response.data.code}`, {
              code: response.data.code,
              success: response.data.success,
              message: response.data.message,
            });

            if (response.data.code === 0 || response.data.code === 200 || response.data.success) {
              const newAccessToken = response.data.data.access_token;
              const newRefreshToken = response.data.data.refresh_token;
              const expiresIn = response.data.data.expires_in;

              pLogger.log('API-REFRESH', `✅ Token refresh succeeded! New token length: ${newAccessToken?.length}, expires_in: ${expiresIn}s`);

              // 更新localStorage中的tokens
              localStorage.setItem('access_token', newAccessToken);
              localStorage.setItem('refresh_token', newRefreshToken);
              localStorage.setItem('expires_in', expiresIn.toString());

              // 更新cookies
              const expires = new Date();
              expires.setSeconds(expires.getSeconds() + expiresIn);
              document.cookie = `access_token=${newAccessToken}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
              document.cookie = `refresh_token=${newRefreshToken}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;

              // 处理队列中的请求
              processQueue(null, newAccessToken);
              isRefreshing = false;

              // 重试原请求
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return apiClient.request(originalRequest);
            } else {
              // 刷新失败，清除并跳转
              pLogger.error('API-REFRESH', '❌ Token refresh failed (bad response code)', {
                code: response.data.code,
                message: response.data.message,
              });
              clearAuthAndLog('401 REFRESH RESPONSE FAILED', originalRequest.url);
              processQueue(new Error('Token refresh failed'), null);
              isRefreshing = false;
              pLogger.error('API-REFRESH', '⚠️⚠️⚠️ Redirecting to /login (refresh failed)');
              window.location.href = '/login';
              return Promise.reject(error);
            }
          } catch (refreshError: any) {
            // 刷新失败，清除并跳转
            pLogger.error('API-REFRESH', '❌ Token refresh exception (request failed)', {
              message: refreshError?.message,
              status: refreshError?.response?.status,
              data: refreshError?.response?.data,
            });
            clearAuthAndLog('401 REFRESH EXCEPTION', originalRequest.url);
            processQueue(refreshError, null);
            isRefreshing = false;
            pLogger.error('API-REFRESH', '⚠️⚠️⚠️ Redirecting to /login (refresh exception)');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// API服务类
class ApiService {
  // GET请求
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.get(url, config);
  }

  // POST请求
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    // 判断是否为公开API（注册、登录、刷新令牌）
    if (this.isPublicEndpoint(url)) {
      return publicApiClient.post(url, data, config);
    }
    return apiClient.post(url, data, config);
  }

  // PUT请求
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.put(url, data, config);
  }

  // PATCH请求
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.patch(url, data, config);
  }

  // DELETE请求
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.delete(url, config);
  }

  // 判断是否为公开API端点
  private isPublicEndpoint(url: string): boolean {
    const publicEndpoints = [
      '/passport/login',           // 统一登录
      '/passport/refresh-token',   // 统一刷新token
      '/passport/register',        // 统一注册
      '/register/customer',        // 客户注册
      '/register/supplier',        // 供应商注册
      '/register/check-availability', // 注册前校验用户名/邮箱是否可用
      '/register/upload-certificate', // 注册时上传证书
      '/register/verify-email',       // 邮箱验证
      '/register/resend-activation-email', // 重新发送激活邮件
      '/api/customer/register',
      '/api/customer/login',
      '/api/customer/refresh-token',
      '/api/supplier/register',
      '/api/supplier/login',
      '/api/supplier/refresh-token',
      '/api/admin/login',
      '/api/admin/refresh-token',
    ];
    return publicEndpoints.some(endpoint => url.includes(endpoint));
  }
}

export const api = new ApiService();
export default apiClient;
